import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Grid,
  Chip,
  Button,
  useTheme,
  Container,
  Fade,
  Slide,
  Grow,
  alpha
} from '@mui/material';
import { 
  Shield, 
  TrendingUp, 
  Users, 
  BarChart3, 
  CheckCircle,
  ArrowRight,
  Zap,
  Award
} from 'lucide-react';

const LandingSection = ({ onGetStarted }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleTryDemo = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      navigate('/score');
    }
  };

  const features = [
    {
      icon: <Shield size={24} />,
      title: "AI-Powered Scoring",
      description: "Advanced algorithms analyze customer data to predict commission potential",
      color: '#3B82F6'
    },
    {
      icon: <TrendingUp size={24} />,
      title: "Real-time Analytics",
      description: "Live insights and performance metrics for better decision making",
      color: '#10B981'
    },
    {
      icon: <Users size={24} />,
      title: "Multi-Product Support",
      description: "Insurance and lending products scoring in one platform",
      color: '#F59E0B'
    },
    {
      icon: <BarChart3 size={24} />,
      title: "Commission Optimization",
      description: "Maximize agent performance with data-driven commission structures",
      color: '#8B5CF6'
    }
  ];

  const benefits = [
    { text: "100% Data Security", icon: <Shield size={16} /> },
    { text: "Reduce manual scoring time by 80%", icon: <Zap size={16} /> },
    { text: "Improve agent performance tracking", icon: <Users size={16} /> },
    { text: "Real-time commission calculations", icon: <Award size={16} /> }
  ];

  return (
    <Box sx={{ mb: 6 }}>
      <Fade in={true} timeout={1000}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            boxShadow: '0 25px 80px rgba(102, 126, 234, 0.3)'
          }}
        >
          <Box sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 2 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box>
                  <Chip
                    label="Demo Mode"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      mb: 2,
                      fontWeight: 600
                    }}
                  />
                  <Typography variant="h2" fontWeight="800" gutterBottom sx={{ 
                    background: 'linear-gradient(45deg, #ffffff 30%, #f1f5f9 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}>
                    Intelligent Commission Scoring
                  </Typography>
                  <Typography variant="h5" sx={{ opacity: 0.95, mb: 4, lineHeight: 1.6, fontWeight: 400 }}>
                    Transform your business with AI-powered customer scoring and commission optimization. 
                    Make data-driven decisions that maximize agent performance and revenue.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleTryDemo}
                      endIcon={<ArrowRight size={20} />}
                      sx={{
                        backgroundColor: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        px: 5,
                        py: 2,
                        fontSize: '1.1rem',
                        borderRadius: 3,
                        boxShadow: '0 8px 25px rgba(255,255,255,0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 35px rgba(255,255,255,0.4)'
                        }
                      }}
                    >
                      Try Demo
                    </Button>
                    {/* <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)'
                        }
                      }}
                    >
                      Learn More
                    </Button> */}
                  </Box>

                  <Slide direction="right" in={true} timeout={800}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Key Benefits:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {benefits.map((benefit, index) => (
                          <Chip
                            key={index}
                            label={benefit.text}
                            size="small"
                            icon={<CheckCircle size={16} />}
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: 'white'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Slide>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Slide direction="left" in={true} timeout={1000}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative'
                  }}>
                    <Box
                      sx={{
                        width: { xs: 250, md: 300 },
                        height: { xs: 250, md: 300 },
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        backdropFilter: 'blur(20px)',
                        border: '2px solid rgba(255,255,255,0.2)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 150, md: 200 },
                          height: { xs: 150, md: 200 },
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          backdropFilter: 'blur(15px)',
                          border: '1px solid rgba(255,255,255,0.3)'
                        }}
                      >
                        <Typography variant="h1" fontWeight="800" sx={{ 
                          background: 'linear-gradient(45deg, #ffffff 30%, #f1f5f9 90%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          95%
                        </Typography>
                        <Typography variant="h6" fontWeight="600" sx={{ opacity: 0.9 }}>
                          Accuracy Rate
                        </Typography>
                      </Box>
                      
                      {/* Floating elements */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '20%',
                          right: '10%',
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: 'float 3s ease-in-out infinite',
                          '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-10px)' }
                          }
                        }}
                      >
                        <TrendingUp size={20} />
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: '15%',
                          left: '15%',
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: 'float2 3s ease-in-out infinite 1s',
                          '@keyframes float2': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-8px)' }
                          }
                        }}
                      >
                        <Shield size={16} />
                      </Box>
                    </Box>
                  </Box>
                </Slide>
              </Grid>
            </Grid>
          </Box>
          
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              zIndex: 1
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              zIndex: 1
            }}
          />
        </Card>
      </Fade>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ mt: 6 }}>
        <Fade in={true} timeout={1200}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" fontWeight="700" gutterBottom color="text.primary">
              Platform Features
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Discover the powerful capabilities that make FinLead AI the leading choice for commission optimization
            </Typography>
          </Box>
        </Fade>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Grow in={true} timeout={800} style={{ transitionDelay: `${index * 200}ms` }}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      borderColor: feature.color,
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                        color: 'white'
                      },
                      '& .feature-icon-bg': {
                        transform: 'scale(1.2)',
                        backgroundColor: feature.color
                      }
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', mb: 3 }}>
                    <Box 
                      className="feature-icon-bg"
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        borderRadius: 3,
                        backgroundColor: alpha(feature.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        mb: 2
                      }}
                    >
                      <Box className="feature-icon" sx={{ color: feature.color, transition: 'all 0.3s ease' }}>
                        {feature.icon}
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="h5" fontWeight="700" gutterBottom color="text.primary">
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                  
                  {/* Background decoration */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -20,
                      right: -20,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: alpha(feature.color, 0.05),
                      zIndex: 0
                    }}
                  />
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingSection;